import React, { useEffect, useState, useRef, useContext } from 'react';
import DataService from '../../XXX/DataService';
import GameAssetsContext from '../../XXX/context/GameAssetContext';
import ComboDisplay from '../../XXX/GameSimulator/GameContainer2/GameGrid/ChampionCard/ComboDisplay/ComboDisplay';
import { championImages } from '../../XXX/GameSimulator/GameContainer2/GameGrid/ChampionCard/ComboDisplay/Events/Images/championImages';
import './SimpleMatchupDisplay.css';

// Simple deep equality check (alternative to lodash.isequal)
const isEqual = (a: any, b: any): boolean => {
  return JSON.stringify(a) === JSON.stringify(b);
};

interface ComboData {
  combo: any;
  damage: number;
  targetMaxHealth: number;
  events: any[];
  isSingleAbility: boolean;
  cached?: boolean;
  simulated?: boolean;
  error?: boolean;
}

interface MatchupCombos {
  mainChampion: {
    name: string;
    level: number;
    combos: ComboData[];
  };
  opponent: {
    name: string;
    level: number;
    combos: ComboData[];
  };
  summary?: {
    total: number;
    cached: number;
    simulated: number;
    uncached: number;
  };
  availableSkills?: string[];
}

interface ChampionConfig {
  name: string;
  level: number;
  critMode?: string; // 'AlwaysCrit' | 'NeverCrit' | 'RandomCrit' | 'ExpectedValue'
  runes: {
    primary: string[];
    secondary: string[];
    shards: number[];
  };
  items: number[];
  skills: {
    q: number;
    w: number;
    e: number;
    r: number;
  };
}

interface SimpleMatchupDisplayProps {
  championConfig: ChampionConfig;
  targetChampionConfig: ChampionConfig | null;
}

const SimpleMatchupDisplay: React.FC<SimpleMatchupDisplayProps> = ({ 
  championConfig, 
  targetChampionConfig
}) => {
  const [matchupCombos, setMatchupCombos] = useState<MatchupCombos | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRawLogs, setShowRawLogs] = useState<Record<number, boolean>>({});
  const [expandedCombos, setExpandedCombos] = useState<Record<number, boolean>>({});
  const [showSingleAbilities, setShowSingleAbilities] = useState(true);
  const [showMultiAbilities, setShowMultiAbilities] = useState(true);
  
  const { championsAssets, fetchAndSetChampionAssets } = useContext(GameAssetsContext);

  // Guard against duplicate API calls
  const lastKey = useRef<string | null>(null);

  // Fetch champion assets
  useEffect(() => {
    if (!championsAssets[championConfig.name]) {
      fetchAndSetChampionAssets(championConfig.name);
    }
    if (targetChampionConfig && !championsAssets[targetChampionConfig.name]) {
      fetchAndSetChampionAssets(targetChampionConfig.name);
    }
  }, [championConfig.name, targetChampionConfig?.name, championsAssets, fetchAndSetChampionAssets, targetChampionConfig]);

  // Toggle controls for individual combos
  const toggleRawLogs = (index: number) => {
    setShowRawLogs(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  
  const toggleExpandCombo = (index: number) => {
    setExpandedCombos(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Function to format damage display with percentage
  const formatDamage = (damage: number, targetMaxHealth?: number) => {
    // Round damage to nearest integer
    const roundedDamage = Math.round(damage);
    
    // Calculate percentage of target's max health if available
    if (targetMaxHealth && targetMaxHealth > 0) {
      const percentage = (damage / targetMaxHealth) * 100;
      return (
        <div>
          <div className="damage-value">{roundedDamage}</div>
          <div className="damage-percentage">{percentage.toFixed(1)}%</div>
        </div>
      );
    }
    
    // Fallback to just rounded damage
    return roundedDamage;
  };



  useEffect(() => {
    console.log(`[SimpleMatchupDisplay] useEffect triggered!`);
    console.log(`[SimpleMatchupDisplay] - targetChampionConfig:`, targetChampionConfig ? 'present' : 'null');
    
    if (!targetChampionConfig) {
      console.log(`[SimpleMatchupDisplay] No target, clearing state`);
      setMatchupCombos(null);
      lastKey.current = null;
      return;
    }

    // build a string key of exactly the fields your API cares about
    const key = JSON.stringify({
      main: {
        name: championConfig.name,
        level: championConfig.level,
        critMode: championConfig.critMode || 'ExpectedValue',
        items: championConfig.items,
        runes: championConfig.runes,
        skills: championConfig.skills,
      },
      target: {
        name: targetChampionConfig.name,
        level: targetChampionConfig.level,
        items: targetChampionConfig.items,
        runes: targetChampionConfig.runes,
        skills: targetChampionConfig.skills,
      }
    });

    console.log(`[SimpleMatchupDisplay] key:`, key);
    console.log(`[SimpleMatchupDisplay] lastKey.current:`, lastKey.current);

    // only fetch if that key really changed
    if (key === lastKey.current) {
      console.log(`[SimpleMatchupDisplay] Key unchanged, skipping fetch`);
      return;
    }
    lastKey.current = key;

    console.log(`[SimpleMatchupDisplay] Making API call for new key`);
    setLoading(true);
    setError(null);
    DataService.fetchMatchupCombos(championConfig, targetChampionConfig)
      .then(data => {
        console.log(`[SimpleMatchupDisplay] API call succeeded:`, data);
        setMatchupCombos(data);
      })
      .catch(err => {
        console.log(`[SimpleMatchupDisplay] API call failed:`, err);
        setError(err instanceof Error ? err.message : 'Failed to fetch combos');
        lastKey.current = null;  // allow a retry
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    // the only things React watches here are the two props—
    // but thanks to React.memo, they only change when the content
    // of championConfig/targetChampionConfig really changes.
    championConfig,
    targetChampionConfig,
  ]);

  if (!targetChampionConfig) {
    return (
      <div className="combo-selector">
        <div className="combo-selector-empty">
          <div>💡 Select a target champion to view combos</div>
          <p>Choose an opponent to see {championConfig.name}'s combo damage calculations.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="combo-selector">
        <div className="combo-selector-loading">
          <div className="spinner-circle"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="combo-selector">
        <div className="combo-selector-error">
          <h3>❌ Error loading combos</h3>
          <p>{error}</p>
          <button 
            onClick={() => {
              lastKey.current = null;
              setError(null);
              // Trigger re-fetch by forcing useEffect
              const event = new Event('retry-fetch');
              window.dispatchEvent(event);
            }}
            className="retry-button"
          >
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  if (!matchupCombos || !matchupCombos.mainChampion.combos.length) {
    return (
      <div className="combo-selector">
        <div className="combo-selector-empty">
          <div>No combos available for {championConfig.name} vs {targetChampionConfig.name}</div>
          <p>This champion configuration may not have combos available at level {championConfig.level}.</p>
          {matchupCombos?.availableSkills && (
            <div className="available-skills">
              <strong>Available skills:</strong> {matchupCombos.availableSkills.join(', ') || 'None'}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Split combos into single abilities and multi-ability combos
  const singleAbilityCombos = matchupCombos.mainChampion.combos.filter(combo => combo.isSingleAbility);
  const multiAbilityCombos = matchupCombos.mainChampion.combos.filter(combo => !combo.isSingleAbility);

  return (
    <div className="combo-selector">
      
      {/* Single Abilities Section */}
      {singleAbilityCombos.length > 0 && (
        <div className="combo-section">
          <div 
            className="section-header" 
            onClick={() => setShowSingleAbilities(!showSingleAbilities)}
          >
            <h4>
              <span className="expand-icon">{showSingleAbilities ? '▼' : '►'}</span>
              Abilities ({singleAbilityCombos.length})
            </h4>
          </div>
          
          {showSingleAbilities && (
            <div className="section-content">
              {singleAbilityCombos.map((combo, index) => (
                <div key={`single-${index}`} className="combo-item">
                  <div 
                    className="combo-header"
                    onClick={() => toggleExpandCombo(index)}
                  >
                    <h4>
                      <span className="expand-icon">
                        {expandedCombos[index] ? '▼' : '►'}
                      </span>
                      <MiniAbilityIcons sequence={combo.combo.sequence} championName={championConfig.name} events={combo.events} />
                      <span className="combo-name">{combo.combo.name || combo.combo.sequence}</span>
                    </h4>
                    <span className="combo-damage">{formatDamage(combo.damage, combo.targetMaxHealth)}</span>
                  </div>
                  
                  {expandedCombos[index] && (
                    <div className="combo-events">
                      <div className="combo-event-controls">
                      {/*   <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRawLogs(index);
                          }}
                          className="show-raw-logs-btn"
                        >
                          {showRawLogs[index] ? 'Hide Raw Logs' : 'Show Raw Logs'}
                        </button> */}
                      </div>
                      
                      <ComboDisplay
                        logs={combo.events}
                        championName={championConfig.name}
                        targetChampionName={targetChampionConfig.name}
                        targetMaxHealth={combo.targetMaxHealth}
                        showDetails={true}
                        showStackingEvents={true}
                        showStatEvents={true}
                        showCCEvents={true}
                      />
                      
                      {showRawLogs[index] && (
                        <div className="raw-logs">
                          <pre>{JSON.stringify(combo.events, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Multi-Ability Combos Section */}
      {multiAbilityCombos.length > 0 && (
        <div className="combo-section">
          <div 
            className="section-header" 
            onClick={() => setShowMultiAbilities(!showMultiAbilities)}
          >
            <h4>
              <span className="expand-icon">{showMultiAbilities ? '▼' : '►'}</span>
              Combos ({multiAbilityCombos.length})
            </h4>
          </div>
          
          {showMultiAbilities && (
            <div className="section-content">
              {multiAbilityCombos.map((combo, index) => (
                <div key={`multi-${index}`} className="combo-item">
                  <div 
                    className="combo-header"
                    onClick={() => toggleExpandCombo(index + 1000)}
                  >
                    <h4>
                      <span className="expand-icon">
                        {expandedCombos[index + 1000] ? '▼' : '►'}
                      </span>
                      <MiniAbilityIcons sequence={combo.combo.sequence} championName={championConfig.name} events={combo.events} />
                      {/* Don't show combo name for multi-ability combos like the static version */}
                    </h4>
                    <span className="combo-damage">{formatDamage(combo.damage, combo.targetMaxHealth)}</span>
                  </div>
                  
                  {expandedCombos[index + 1000] && (
                    <div className="combo-events">
                      {combo.combo.description && (
                        <div className="combo-description">
                          {combo.combo.description}
                        </div>
                      )}
                  {/*     <div className="combo-event-controls">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRawLogs(index + 1000);
                          }}
                          className="show-raw-logs-btn"
                        >
                          {showRawLogs[index + 1000] ? 'Hide Raw Logs' : 'Show Raw Logs'}
                        </button>
                      </div> */}
                      
                      <ComboDisplay
                        logs={combo.events}
                        championName={championConfig.name}
                        targetChampionName={targetChampionConfig.name}
                        targetMaxHealth={combo.targetMaxHealth}
                        showDetails={true}
                        showStackingEvents={true}
                        showStatEvents={true}
                        showCCEvents={true}
                      />
                      
                      {showRawLogs[index + 1000] && (
                        <div className="raw-logs">
                          <pre>{JSON.stringify(combo.events, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Helper function to extract base ability from duration-enhanced sequences
const extractBaseAbility = (skill: string): string => {
  // Handle wait/delay notation (_:1.5 or just _)
  if (skill.startsWith('_')) {
    return '_';
  }
  
  // Handle cast time break (T:0.5)
  if (skill.startsWith('T:')) {
    return 'T';
  }
  
  // Handle blocking hold notation (E[3], Q[max], R[min])
  const blockingHoldMatch = skill.match(/^([QWERA])\[/);
  if (blockingHoldMatch) {
    return blockingHoldMatch[1];
  }
  
  // Handle non-blocking toggle notation (R::4, Q::max)
  const nonBlockingToggleMatch = skill.match(/^([QWERA])::/);
  if (nonBlockingToggleMatch) {
    return nonBlockingToggleMatch[1];
  }
  
  // Handle explicit recast format (E:3E, Q:2Q, etc.) — legacy
  const explicitRecastMatch = skill.match(/^([QWERA]):(\d+\.?\d*|\w+)\1$/);
  if (explicitRecastMatch) {
    return explicitRecastMatch[1];
  }
  
  // Handle duration format (E:3, Q:max, E:min, etc.) — legacy
  if (skill.includes(':')) {
    const [ability] = skill.split(':');
    if (['Q', 'W', 'E', 'R', 'A'].includes(ability)) {
      return ability;
    }
  }
  
  // Return as-is if no duration syntax found
  return skill;
};

// Helper: extract KeyPress events from logs in sequence order
const extractKeyPressEventsFromLogs = (events: any[]): Array<{ability: string, imageState: string, championName: string}> => {
  if (!events || events.length === 0) return [];
  
  const keyPressEvents: Array<{ability: string, imageState: string, championName: string}> = [];
  
  const traverseEvents = (eventList: any[]) => {
    eventList.forEach(event => {
      if (event.type === 'KeyPress') {
        const abilityKey = event.details?.abilityKey?.toUpperCase() || '';
        const imageState = event.imageState || 'default';
        const champName = event.details?.championName || '';
        
        if (abilityKey) {
          keyPressEvents.push({ ability: abilityKey, imageState, championName: champName });
        }
      }
      
      if (event.childEvents && event.childEvents.length > 0) {
        traverseEvents(event.childEvents);
      }
    });
  };
  
  traverseEvents(events);
  return keyPressEvents;
};

// Mini component to display ability icons in a sequence, using imageState from events when available
const MiniAbilityIcons: React.FC<{
  sequence: string;
  championName: string;
  events?: any[];
}> = ({ sequence, championName, events }) => {
  const { championsAssets } = useContext(GameAssetsContext);
  
  // Try to extract KeyPress events from logs first
  const keyPressEvents = events ? extractKeyPressEventsFromLogs(events) : [];
  
  // Fallback: extract individual abilities from sequence
  const fallbackAbilities = sequence
    .split(/[,\s]/)
    .filter(Boolean)
    .map(ability => {
      const trimmed = ability.trim().toUpperCase();
      return extractBaseAbility(trimmed);
    })
    .filter(ability => ability !== '_' && ability !== 'T');

  // Return if assets aren't loaded yet
  if (!championsAssets[championName]) {
    return <span className="mini-sequence-text">{sequence}</span>;
  }
  
  const spellImageUrls = championsAssets[championName].spellImageUrls;
  const autoAttackImageUrl = championsAssets[championName].autoAttackImageUrl;
  
  // Use KeyPress events if available, otherwise fallback to sequence parsing
  const abilitiesToRender = keyPressEvents.length > 0 ? keyPressEvents : fallbackAbilities.map(ability => ({
    ability,
    imageState: 'default',
    championName
  }));
  
  return (
    <div className="mini-ability-sequence">
      {abilitiesToRender.map((abilityData, idx) => {
        const ability = typeof abilityData === 'string' ? abilityData : abilityData.ability;
        const imageState = typeof abilityData === 'string' ? 'default' : abilityData.imageState;
        let iconUrl = '';
        
        // For abilities (not auto attacks), try championImages with imageState first
        if (ability !== 'A' && championName && championImages[championName]) {
          const champMap = championImages[championName];
          
          // 1) If imageState != "default", try championImages[championName][imageState]
          if (imageState && imageState !== "default") {
            iconUrl = champMap[imageState.toLowerCase()];
          }
          
          // 2) Fallback to champMap[abilityKey]
          if (!iconUrl) {
            iconUrl = champMap[ability.toLowerCase()];
          }
        }
        
        // Fallback to base spell images
        if (!iconUrl) {
          switch (ability) {
            case 'Q':
              iconUrl = spellImageUrls.Q;
              break;
            case 'W':
              iconUrl = spellImageUrls.W;
              break;
            case 'E':
              iconUrl = spellImageUrls.E;
              break;
            case 'R':
              iconUrl = spellImageUrls.R;
              break;
            case 'A':
              iconUrl = autoAttackImageUrl;
              break;
            default:
              return <span key={`ability-${idx}`} className="unknown-ability">{ability}</span>;
          }
        }
        
        return (
          <React.Fragment key={`ability-fragment-${idx}`}>
            <div className="mini-ability-icon">
              <img src={iconUrl} alt={ability} />
              <span className="mini-ability-letter">{ability}</span>
            </div>
            {/* Add arrow between icons, but not after the last one */}
            {idx < abilitiesToRender.length - 1 && <span className="ability-arrow">→</span>}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default React.memo(SimpleMatchupDisplay, (prev, next) => {
  // shallow out if there's no opponent both times
  if (!prev.targetChampionConfig && !next.targetChampionConfig) {
    return true;
  }
  // deep-compare both championConfig and targetChampionConfig
  return (
    isEqual(prev.championConfig, next.championConfig) &&
    isEqual(prev.targetChampionConfig, next.targetChampionConfig)
  );
}); 